import React, { useMemo, useState } from 'react';

import { Image, LoginGateBlock } from '@/components';
import { useIsMobile, useStore } from '@/hooks';
import CouponService from '@/lib/CouponService';
import { trackEvent } from '@/lib/GA';
import PublicEnvironment from '@/stores/PublicEnvironment';

import styles from './styles.module.scss';

interface Props {
  offer: OfferFragment;
}

const ClaimOfferBlock = ({ offer }: Props) => {
  const [claimed, setClaimed] = useState(false);
  const [isMobile] = useIsMobile(768);
  const [type, setType] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const { loggedIn, neatIdeasToken } = useStore('userStore');

  const claimButton = useMemo(() => {
    const handleClick = async () => {
      trackEvent('Offers', 'Offer', 'Claim offer button clicked');

      if (offer.id) {
        // setLoading(true);
        const res = await CouponService.getCoupon(offer.id);
        if (!res?.data || !res.success) {
          setError(res?.data?.message ?? 'Internal error');
        } else {
          const coupon = res.data;

          if (coupon.code && coupon.type) {
            setCode(coupon.code);
            setType(coupon.type);
          } else {
            setCode('n/a');
          }
        }
      }
      setClaimed(true);
      // setLoading(false);
    };

    if (!claimed) {
      return (
        <button onClick={handleClick} data-testid="claimButton">
          Claim Offer
        </button>
      );
    }

    if (error) return <div data-testid="claimError">{error}</div>;

    switch (type) {
      case 'code':
        return (
          <div data-testid="claimCode">
            <p>CODE: {code}</p>
          </div>
        );
      case 'link':
        // If the website URL is neat ideas, use the SSO handover link.
        let url = code;
        const neatIdeasStore = PublicEnvironment.get('NEAT_IDEAS_STORE');
        if (code?.includes(neatIdeasStore)) {
          url = `https://${neatIdeasStore}/mymotorewards/handoversso/index/ectoken/key/${neatIdeasToken}?cat=&parameter=&url=${code}`;
        }
        return (
          <a data-testid="claimLink" href={url} target="_blank" rel="noopener noreferrer">
            Visit site to redeem
          </a>
        );
      default:
        return <div data-testid="claimShow">Present card to redeem this offer</div>;
    }
  }, [claimed, error, type, offer.id, code, neatIdeasToken]);

  if (!loggedIn) {
    return <LoginGateBlock>You must be a member to claim this offer</LoginGateBlock>;
  }

  return !isMobile ? (
    <div className={styles.offerBlock}>
      <h3>Claim this offer</h3>
      {offer?.logos?.[0]?.url && (
        <div className={styles.logo}>
          <Image {...offer.logos?.[0]} />
        </div>
      )}
      <div className={styles.offerDiscount} dangerouslySetInnerHTML={{ __html: offer.offerDiscount || '' }} />
      <div className={styles.claimButton}>{claimButton}</div>
    </div>
  ) : (
    <div className={styles.mobileOfferBlock}>
      <h3>Claim this offer</h3>
      <div className={styles.claimButton}>{claimButton}</div>
    </div>
  );
};

export default ClaimOfferBlock;
